import { AppNS } from 'app';
import { useState } from 'react';

import { Box, FormHelperText } from '@mui/material';
import { UppyFile } from '@uppy/core';

import FileUpload from '../file-upload/FileUpload';
import FileUploadedInfo2 from '../file-uploaded-info2/FileUploadedInfo2';
import PhotoThumbnails from './PhotoThumbnails';
import SignatureView from './SignatureView';

const getIndexById = (id: string, fields: AppNS.Params[]): number => {
  return fields.findIndex((item) => item.documentId === id || item.uppyId === id || item.id === id);
};

const DEFAULT_COMPRESSOR_OPTS = {
  maxWidth: 2048,
  maxHeight: 2048,
  mimeType: 'image/jpeg',
};

const matchId = (item: FileInfo, id: string): boolean => {
  return item.documentId === id || item.uppyId === id || item.id === id;
};

export type FileInfoState = 'removing' | 'uploading' | 'uploaded' | 'cancelled' | 'error' | 'saving';

export interface FileInfo {
  id?: string;

  // ID from database, because `id` may be be overwritten by react hook form (if used)
  documentId?: string;

  name?: string;
  thumbnail?: string;
  uppyId: string;
  url?: string;
  state: FileInfoState;
  uploadProgress: number;
  previewUrl?: string;
}

export type MultiFileUploaderType = 'images' | 'documents' | 'signature';

interface MultiFileUploaderProps {
  uploaderId: string;
  collection: FileInfo[];
  collectionLimit: number;

  // when for example parent form is submitted
  isLoading: boolean;
  type: MultiFileUploaderType;
  canRemoveFiles?: boolean;

  modelName?: string;

  filesListPosition?: 'above' | 'below';

  isDisabled?: boolean;
  rawFilesToUpload?: File[];
  reset?: number;

  dragDropVisible?: boolean;

  fileAddAction?: (info: FileInfo) => Promise<any>;
  fileRemoveAction?: (id: string) => Promise<any>;

  onChange: (files: FileInfo[]) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
  onFileRemoved?: (id: string) => void;

  isWithCompressor?:
    | boolean
    | { maxWidth: number; maxHeight: number; convertSize: number; convertTypes: string[]; strict: boolean };
}

export default function MultiFileUploader2(props: MultiFileUploaderProps) {
  const {
    collection,
    collectionLimit,
    type,
    isLoading,
    canRemoveFiles,
    uploaderId,

    modelName = 'document',
    filesListPosition = 'above',

    fileAddAction,
    fileRemoveAction,

    onChange,
    onUploadStart,
    onUploadEnd,
    isDisabled = false,
    onFileRemoved,

    rawFilesToUpload,
    reset,
    dragDropVisible = true,
    isWithCompressor = true,
  } = props;

  const [isUploadingError, setIsUploadingError] = useState<string>('');
  const [isInternalLoading, setIsInternalLoading] = useState<boolean>(false);

  const [files, setFiles] = useState<FileInfo[]>([]);
  const [idToRemove, setIdToRemove] = useState<string>('');
  const [idToRetry, setIdToRetry] = useState<string>('');

  const onFileRemove = async (id: string) => {
    const index = getIndexById(id, files);

    if (index !== -1) {
      setIsInternalLoading(true);
      setFiles((currentState) => {
        return currentState.map((file, i) => {
          if (i === index) {
            return { ...file, state: 'removing' };
          }
          return file;
        });
      });

      if (fileRemoveAction && files[index].documentId) {
        await fileRemoveAction(files[index].documentId || '');
      }
      setFiles((currentState) => {
        const state = currentState.filter((file, i) => {
          return i !== index;
        });

        onChange(state);
        return state;
      });

      setIsInternalLoading(false);
      onFileRemoved && onFileRemoved(id);
    }
  };

  const onFileAdd = async (file: FileInfo) => {
    setFiles((currentState) => {
      return currentState.map((currentStateItem) => {
        if (matchId(currentStateItem, file.uppyId)) {
          return {
            ...currentStateItem,
            uppyId: file.uppyId,
            state: 'saving',
            uploadProgress: 100,
            // set `previewUrl` to keep browser file preview because after upload
            // we don't get proper URL when using ACL auth
            previewUrl: currentStateItem.url,
          };
        }
        return currentStateItem;
      });
    });

    let output = {} as FileInfo;
    if (fileAddAction) {
      try {
        output = await fileAddAction(file);
      } catch (error) {}
    } else {
      output['state'] = 'uploaded';
    }

    setFiles((currentState) => {
      const state = currentState.map((currentStateItem, i) => {
        if (matchId(currentStateItem, file.uppyId)) {
          return {
            ...currentStateItem,
            // we take url from `output` because it's value that we pass to DB
            ...output,
            // output doesn't have uppyId
            uppyId: file.uppyId,
            state: Object.keys(output).length > 0 ? 'uploaded' : 'error',
            uploadProgress: Object.keys(output).length > 0 ? 100 : 0,
          } as FileInfo;
        }
        return currentStateItem;
      });

      onChange(state);
      return state;
    });
  };

  const onFileProgress = (id: string, uploadProgress: number) => {
    setFiles((currentState) => {
      return currentState.map((currentStateItem, i) => {
        if (matchId(currentStateItem, id)) {
          return {
            ...currentStateItem,
            uploadProgress,
          };
        }
        return currentStateItem;
      });
    });
  };

  const onFileError = (id: string) => {
    setFiles((currentState) => {
      return currentState.map((currentStateItem) => {
        if (matchId(currentStateItem, id)) {
          return {
            ...currentStateItem,
            state: 'error',
            uploadProgress: 0,
          };
        }
        return currentStateItem;
      });
    });
  };

  const onFileRemovedBySystem = (id: string) => {
    setFiles((currentState) => {
      return currentState.filter((currentStateItem, i) => matchId(currentStateItem, id));
    });
  };

  const onFileCreate = (file: FileInfo) => {
    setFiles((currentState) => {
      return [file, ...currentState];
    });
  };

  const onFileRetry = (id: string) => {
    setFiles((currentState) => {
      return currentState.map((currentStateItem) => {
        if (matchId(currentStateItem, id)) {
          return {
            ...currentStateItem,
            state: 'uploading',
          };
        }
        return currentStateItem;
      });
    });
  };

  const addFile = (file: UppyFile) => {
    const id = file?.meta?.documentId || file.id || '';
    const isInitial = file?.meta?.isInitial || false;

    onFileCreate({
      documentId: (file?.meta?.documentId as string) || '',
      name: file.name,
      uppyId: file.id,
      id: id as string,
      state: isInitial ? 'uploaded' : 'uploading',
      uploadProgress: isInitial ? 100 : 0,
      url: (file?.meta?.url as string) || URL.createObjectURL(file.data) || '',
    });
  };

  const onSingleFileUploaded = (name: string, uppyId: string, url: string) => {
    onFileAdd({
      name,
      uppyId,
      url,
      state: 'uploaded',
      uploadProgress: 100,
    });
  };

  const handleRemove = (id: string) => {
    const index = getIndexById(id, files);
    setIdToRemove(files[index].uppyId);
    onFileRemove(id);
  };

  const handleRetry = (id: string) => {
    const index = getIndexById(id, files);
    setIdToRetry(files[index].uppyId);
    onFileRetry(id);
  };

  const compressorOpts =
    typeof isWithCompressor === 'object' ? isWithCompressor : isWithCompressor ? DEFAULT_COMPRESSOR_OPTS : false;

  const uploadConfig = {
    restrictionsNote: 'Pdf or Image files. Max size 50MB.',
    restrictionsRules: {
      maxNumberOfFiles: collectionLimit,
      maxFileSize: 52414400, // 50 MB
      allowedFileTypes: ['image/*', 'application/pdf'],
    },
    presignedUrl: {
      modelName,
      propertyName: 'file',
    },
    isWithThumbnail: type === 'images',
    isWithCompressor: compressorOpts,
    isWithStatusBar: true,
    onUploadStart: () => {
      setIsUploadingError('');
      onUploadStart && onUploadStart();
    },
    onFileAdded: (file: UppyFile) => {
      addFile(file);
    },
    onFileRemoved: (file: UppyFile) => {
      // cancel all for example
      onFileRemovedBySystem(file.id);
    },
    onUploadSuccess: (file: UppyFile, response: any) => {
      onSingleFileUploaded(file.name, file.id, response.uploadURL);
    },
    onUploadEnd: () => {
      onUploadEnd && onUploadEnd();
    },
    onCancelAll: () => {
      onUploadEnd && onUploadEnd();
    },
    onRestrictionsFailed: (message: string) => {
      setIsUploadingError(message);
    },
    onThumbnailGenerated: (preview: string, file: any) => {
      // addThumbnail(preview, file.id, false);
    },
    onFileProgress: (id: string, uploaded: number, total: number) => {
      onFileProgress(id, Math.floor((uploaded / total) * 100));
    },
    onFileError: (file: UppyFile | undefined) => {
      if (file) {
        onFileError(file.id);
      }
      // prevents uploader status lock
      onUploadEnd && onUploadEnd();
      //  reset retry
      setIdToRetry('');
    },
  };

  return (
    <div>
      {filesListPosition === 'above' && (
        <>
          {type === 'images' && files.length > 0 && (
            <PhotoThumbnails data={files} onImageRemove={handleRemove} isUploadActive={false} />
          )}
          {type === 'signature' && files.length > 0 && (
            <SignatureView
              data={files[0]}
              onFileRemove={handleRemove}
              canRemoveFiles={canRemoveFiles}
              isLoading={isInternalLoading}
            />
          )}
          {type === 'documents' && files.length > 0 && (
            <FileUploadedInfo2
              onFileRemove={handleRemove}
              onFileRetry={handleRetry}
              fields={files}
              canRemoveFiles={canRemoveFiles}
            />
          )}
        </>
      )}

      {/* Set initial state inside the Uppy to have control over all files in the same way */}
      <FileUpload
        config={uploadConfig}
        id={uploaderId}
        idToRemove={idToRemove}
        idToRetry={idToRetry}
        initialState={collection}
        dragDropVisible={files.length < collectionLimit && dragDropVisible}
        isDisabled={isLoading}
        rawFilesToUpload={rawFilesToUpload}
        reset={reset}
      />
      {isUploadingError && (
        <FormHelperText className="Mui-error">
          {isUploadingError || 'File exceeds max size or file is improper type.'}
        </FormHelperText>
      )}

      {filesListPosition === 'below' && (
        <Box mt={2}>
          {type === 'images' && files.length > 0 && (
            <PhotoThumbnails data={files} onImageRemove={handleRemove} isUploadActive={false} />
          )}
          {type === 'signature' && files.length > 0 && (
            <SignatureView
              data={files[0]}
              onFileRemove={handleRemove}
              canRemoveFiles={canRemoveFiles}
              isLoading={isInternalLoading}
            />
          )}
          {type === 'documents' && files.length > 0 && (
            <FileUploadedInfo2
              onFileRemove={handleRemove}
              onFileRetry={handleRetry}
              fields={files}
              canRemoveFiles={canRemoveFiles}
            />
          )}
        </Box>
      )}
    </div>
  );
}
